import { arrayOf, bool, func, object, string } from "prop-types";
import React, { useRef } from "react";
import { HexColorInput, HexColorPicker } from "react-colorful";

import styled from "styled-components/macro";

import FnrTypography from "FnrTypography";
import ErrorMessage from "Signups/FamilyRegistration/Shared/ErrorMessage";

import FnrButton from "UI/FnrButton3/FnrButton";
import { FnrPopover } from "UI/FnrPopover2";

import { ensureLeadingHex } from "Utilities/Colors/colorUtils";
import useEventListener from "Utilities/useEventListener";

import GradientSelector from "./GradientSelector";

/**
 * @param {object} props
 * @param {string} props.selectedColor - The selected color in hex format.
 * @param {string} props.color - The color in hex format.
 * @param {function} props.setColor - Function to set the color.
 * @param {function} props.save - Function to save the selected color.
 * @param {boolean} props.disabled - Whether the button is disabled.
 * @param {object} props.error - Error object containing error message.
 * @param {boolean} props.show - Whether the color picker is shown.
 * @param {function} props.setShow - Function to set the visibility of the color picker.
 * @param {boolean} props.saving - Whether the save action is in progress.
 * @param {string} props.ariaLabel - Aria label for accessibility.
 * @param {string} props.popoverColor - Color of the popover.
 * @param {boolean} props.showGradientSelector - Whether to show the gradient selector. onSelectGradient and initialGradient required for this to work properly. See existing examples.
 * @param {function} props.onSelectGradient - Function to handle gradient selection.
 * @param {array} props.initialGradient - Initial gradient colors.
 * @returns
 */

export default function TempColorPicker({
  selectedColor,
  color,
  setColor,
  save,
  disabled,
  error,
  show,
  setShow,
  saving,
  ariaLabel = "Color Picker",
  popoverColor = "blue",
  showGradientSelector = false,
  onSelectGradient,
  initialGradient,
}) {
  const wrapperRef = useRef();
  const buttonRef = useRef();

  color = ensureLeadingHex(color);
  selectedColor = ensureLeadingHex(selectedColor);

  function toggleShow() {
    setShow((show) => !show);
  }

  // close the popover when user types the escape key
  useEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      setShow(false);
    }
  });

  return (
    <Wrapper>
      <ColorBoxButton
        ref={buttonRef}
        onClick={toggleShow}
        ariaLabel={ariaLabel}
      >
        <ColorBox $color={selectedColor} />
      </ColorBoxButton>
      <FnrPopover
        show={show}
        buttonRef={buttonRef}
        onShouldClose={() => setShow(false)}
        color={popoverColor}
        thinBorder
        style={{ overflow: "visible" }}
      >
        <PickerWrapper ref={wrapperRef}>
          <HexColorPicker color={color} onChange={setColor} />
          <HexColorInput
            aria-label="color-picker-hex-code-input"
            prefixed={true}
            color={color}
            onChange={setColor}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                save();
              }
            }}
            autoFocus={true}
            style={{
              border: "1px solid #ccc",
              marginTop: "5px",
              borderRadius: "8px",
              backgroundColor: "#efefef",
              padding: "3px",
              width: "200px",
              boxSizing: "border-box",
              fontSize: "18px",
              fontWeight: 400,
              textAlign: "center",
            }}
          />

          {showGradientSelector && (
            <GradientSelectorWrapper>
              {/* <SelectorHeader type="accent">Select a Background</SelectorHeader> */}
              <GradientSelector
                color={color}
                initialGradient={initialGradient}
                onSelect={onSelectGradient}
              />
            </GradientSelectorWrapper>
          )}

          <ErrorWrapper hasError={error?.error?.length > 0}>
            {error ? (
              <ErrorMessage error={error?.error} marginLeft="0px" />
            ) : null}
          </ErrorWrapper>

          <FnrButton
            data-cy="TempColorPicker_SaveButton"
            onClick={save}
            size="small"
            color="blue"
            disabled={disabled}
            loading={saving}
            style={{ marginTop: "5px" }}
          >
            Save
          </FnrButton>
        </PickerWrapper>
      </FnrPopover>
    </Wrapper>
  );
}

TempColorPicker.propTypes = {
  selectedColor: string.isRequired,
  color: string.isRequired,
  setColor: func.isRequired,
  save: func.isRequired,
  disabled: bool,
  error: object,
  show: bool.isRequired,
  setShow: func.isRequired,
  saving: bool,
  ariaLabel: string,
  popoverColor: string,
  showGradientSelector: bool,
  onSelectGradient: func,
  initialGradient: arrayOf(string),
};

const Wrapper = styled.div``;

const ColorBoxButton = styled.button`
  width: 30px;
  height: 30px;
  border: 4px solid #fff;
  border-radius: 5px;
  box-shadow: 0 0 5px hsl(0 0% 0% / 0.5);
  padding: 0;
  cursor: pointer;
`;
const ColorBox = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${({ $color }) => $color};
  border-radius: 3px;
`;
const PickerWrapper = styled.div`
  width: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #fff;
  border-radius: 10px;
  padding: 8px;
`;
const ErrorWrapper = styled.div`
  margin-block: ${(p) => (p.hasError ? "5px" : "0px")};
  transition: margin-block 0.2s ease-in-out;
`;
const GradientSelectorWrapper = styled.div`
  width: 100%;
  margin: 1rem 0 0.75rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
const SelectorHeader = styled(FnrTypography)`
  font-family: "Open Sans", sans-serif;
`;